"use client";

import type React from "react";
import { useState } from "react";
import {
  FormControl,
  FormLabel,
  Text,
  Switch,
  HStack,
  Box,
  VStack,
} from "@chakra-ui/react";
import { useAppTheme } from "../../../ThemeContext.tsx";
import { BackchannelFrequencySlider } from "./BackchannelFrequencySlider.tsx";
import { BackchannelWords } from "./BackchannelWords.tsx";

interface BackchannelingToggleProps {
  value: boolean | undefined;
  frequency?: number;
  words?: string[] | null;
  onChange: (value: boolean) => Promise<boolean> | void;
  onFrequencyChange: (value: number) => Promise<boolean> | void;
  onWordsChange: (value: string[] | null) => Promise<boolean> | void;
}

export const BackchannelingToggle: React.FC<BackchannelingToggleProps> = ({
  value = false,
  frequency = 0.8,
  words = null,
  onChange,
  onFrequencyChange,
  onWordsChange,
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsUpdating(true);
    try {
      await onChange(e.target.checked);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <VStack spacing={4} align="stretch">
      <FormControl>
        <HStack justify="space-between" align="flex-start">
          <Box>
            <FormLabel
              fontSize="sm"
              fontWeight="medium"
              color={isDark ? "gray.300" : "gray.700"}
              mb={1}
            >
              Enable Backchanneling
            </FormLabel>
            <Text fontSize="xs" color={isDark ? "gray.400" : "gray.600"}>
              Enables the agent to use affirmations like 'yeah' or 'uh-huh'
              during conversations, indicating active listening and engagement.
            </Text>
          </Box>
          <Switch
            isChecked={value}
            onChange={handleChange}
            isDisabled={isUpdating}
            colorScheme={isDark ? "whiteAlpha" : "blackAlpha"}
            borderColor={isDark ? "gray.500" : "gray.400"}
            sx={{
              ".chakra-switch__track": {
                backgroundColor: value
                  ? isDark
                    ? "gray.600"
                    : "gray.400" // Darker color when turned on
                  : isDark
                  ? "gray.700"
                  : "gray.300", // Color when turned off
              },
            }}
          />
        </HStack>
      </FormControl>

      {value && (
        <VStack spacing={4} align="stretch" pl={4}>
          <BackchannelFrequencySlider
            value={frequency}
            onChange={onFrequencyChange}
          />
          <BackchannelWords value={words} onChange={onWordsChange} />
        </VStack>
      )}
    </VStack>
  );
};
